import {
  MAXIMUM_SAVED_VIEW_COLLABORATORS,
  type AddSavedViewCollaboratorRequest,
  type SavedView,
  type SavedViewCollaborator,
  type SavedViewPermission,
  type UpdateSavedViewCollaboratorRequest,
} from "@meta-video/contracts";
import {
  Alert,
  Button,
  Drawer,
  Group,
  Loader,
  NativeSelect,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type RefObject,
} from "react";

import { restoreFocus } from "../../app/a11y.js";
import { SessionExpiredError } from "../../shared/api/client.js";
import { useProductAnalytics } from "../../shared/analytics/useProductAnalytics.js";
import {
  SavedViewApiError,
  addSavedViewCollaborator,
  getSavedViewCollaborators,
  removeSavedViewCollaborator,
  updateSavedViewCollaborator,
} from "./api.js";

export interface SavedViewCollaboratorsApi {
  readonly addSavedViewCollaborator: (
    viewId: string,
    input: AddSavedViewCollaboratorRequest,
  ) => Promise<SavedViewCollaborator>;
  readonly getSavedViewCollaborators: (
    viewId: string,
  ) => Promise<readonly SavedViewCollaborator[]>;
  readonly removeSavedViewCollaborator: (
    viewId: string,
    collaboratorUserId: string,
  ) => Promise<void>;
  readonly updateSavedViewCollaborator: (
    viewId: string,
    collaboratorUserId: string,
    input: UpdateSavedViewCollaboratorRequest,
  ) => Promise<SavedViewCollaborator>;
}

const defaultApi: SavedViewCollaboratorsApi = {
  addSavedViewCollaborator,
  getSavedViewCollaborators,
  removeSavedViewCollaborator,
  updateSavedViewCollaborator,
};

export interface SavedViewCollaboratorsProps {
  readonly api?: SavedViewCollaboratorsApi;
  readonly fallbackFocusRef?: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
  readonly onReconcile: () => void | Promise<void>;
  readonly onSessionExpired?: () => void;
  readonly opened: boolean;
  readonly returnFocusRef?: RefObject<HTMLElement | null>;
  readonly view: SavedView | null;
}

type Mutation =
  | { readonly kind: "add" }
  | { readonly kind: "remove"; readonly userId: string }
  | { readonly kind: "update"; readonly userId: string };

function isRevoked(error: unknown): boolean {
  return error instanceof SavedViewApiError
    && error.code === "SAVED_VIEW_NOT_FOUND";
}

function messageFor(error: unknown, operation: Mutation["kind"] | "load"): string {
  if (
    operation === "add"
    && error instanceof SavedViewApiError
    && (
      error.code === "SAVED_VIEW_INVITE_UNAVAILABLE"
      || error.code === "SAVED_VIEW_CONFLICT"
    )
  ) {
    return "This person could not be added. Check the address or try again later.";
  }
  if (error instanceof SavedViewApiError) return error.message;
  return operation === "load"
    ? "Collaborators could not be loaded."
    : "Access could not be changed. Try again.";
}

function replaceCollaborator(
  collaborators: readonly SavedViewCollaborator[],
  changed: SavedViewCollaborator,
): readonly SavedViewCollaborator[] {
  return collaborators.map((candidate) =>
    candidate.userId === changed.userId ? changed : candidate);
}

export function SavedViewCollaborators({
  api = defaultApi,
  fallbackFocusRef,
  onClose,
  onReconcile,
  onSessionExpired,
  opened,
  returnFocusRef,
  view,
}: SavedViewCollaboratorsProps) {
  const analytics = useProductAnalytics();
  const [collaborators, setCollaborators] = useState<readonly SavedViewCollaborator[]>([]);
  const [confirmingRemoval, setConfirmingRemoval] = useState<SavedViewCollaborator | null>(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mutation, setMutation] = useState<Mutation | null>(null);
  const [permission, setPermission] = useState<SavedViewPermission>("VIEW");
  const emailRef = useRef<HTMLInputElement>(null);
  const confirmRemovalRef = useRef<HTMLButtonElement>(null);
  const removalTriggerRef = useRef<HTMLButtonElement | null>(null);
  const activeTargetRef = useRef<string | null>(null);
  const mutationRef = useRef<symbol | null>(null);
  const openedOwnerIdRef = useRef<string | null>(null);
  const ownerView = view?.accessRole === "OWNER" ? view : null;
  const pending = mutation !== null;

  useEffect(() => {
    if (confirmingRemoval !== null) confirmRemovalRef.current?.focus();
  }, [confirmingRemoval]);

  const reconcileRevocation = async () => {
    await onReconcile();
    onClose();
  };

  useEffect(() => {
    if (!opened) {
      openedOwnerIdRef.current = null;
      return;
    }
    if (ownerView !== null) {
      openedOwnerIdRef.current = ownerView.id;
      return;
    }
    if (openedOwnerIdRef.current === null) return;
    openedOwnerIdRef.current = null;
    setCollaborators([]);
    setConfirmingRemoval(null);
    setError(null);
    void onReconcile();
    onClose();
  }, [onClose, onReconcile, opened, ownerView?.id]);

  useEffect(() => {
    if (!opened || ownerView === null) return;
    let current = true;
    activeTargetRef.current = ownerView.id;
    mutationRef.current = null;
    setCollaborators([]);
    setConfirmingRemoval(null);
    setEmail("");
    setError(null);
    setLoadFailed(false);
    setLoading(true);
    setMutation(null);
    setPermission("VIEW");
    void api.getSavedViewCollaborators(ownerView.id)
      .then((next) => {
        if (current) setCollaborators(next);
      })
      .catch((caught: unknown) => {
        if (!current) return;
        setLoadFailed(true);
        if (caught instanceof SessionExpiredError) {
          onSessionExpired?.();
        } else if (isRevoked(caught)) {
          void reconcileRevocation();
        } else {
          setError(messageFor(caught, "load"));
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
      if (activeTargetRef.current === ownerView.id) {
        activeTargetRef.current = null;
        mutationRef.current = null;
      }
    };
  }, [api, opened, ownerView?.id]);

  const runMutation = async (
    context: Mutation,
    viewId: string,
    request: () => Promise<SavedViewCollaborator | void>,
    apply: (result: SavedViewCollaborator | void) => void,
  ) => {
    if (mutationRef.current !== null) return;
    const token = Symbol(context.kind);
    mutationRef.current = token;
    const isCurrent = () =>
      mutationRef.current === token && activeTargetRef.current === viewId;
    setError(null);
    setMutation(context);
    try {
      const result = await request();
      if (isCurrent()) apply(result);
      await onReconcile();
      analytics.savedViewAction(
        context.kind === "add"
          ? "add-collaborator"
          : context.kind === "remove"
            ? "remove-collaborator"
            : "update",
      );
    } catch (caught) {
      if (caught instanceof SessionExpiredError) {
        onSessionExpired?.();
      } else if (isRevoked(caught)) {
        await onReconcile();
        if (isCurrent()) onClose();
      } else if (isCurrent()) {
        setError(messageFor(caught, context.kind));
      }
    } finally {
      if (mutationRef.current === token) {
        mutationRef.current = null;
        setMutation(null);
      }
    }
  };

  const submitAdd = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      ownerView === null
      || loadFailed
      || loading
      || pending
      || mutationRef.current !== null
      || ownerView.collaboratorCount >= MAXIMUM_SAVED_VIEW_COLLABORATORS
    ) return;
    const exactEmail = email.trim();
    if (exactEmail.length === 0) return;
    const viewId = ownerView.id;
    void runMutation({ kind: "add" }, viewId, () =>
      api.addSavedViewCollaborator(viewId, {
        email: exactEmail,
        permission,
      }), (added) => {
        setCollaborators((current) => [
          ...current,
          added as SavedViewCollaborator,
        ]);
        setEmail("");
        setPermission("VIEW");
        emailRef.current?.focus();
      });
  };

  const atCapacity = ownerView !== null
    && ownerView.collaboratorCount >= MAXIMUM_SAVED_VIEW_COLLABORATORS;

  return (
    <Drawer
      closeButtonProps={{
        "aria-label": "Close collaborator management",
      }}
      data-full-screen-mobile="true"
      onClose={() => {
        activeTargetRef.current = null;
        mutationRef.current = null;
        setMutation(null);
        onClose();
      }}
      onEnterTransitionEnd={() => emailRef.current?.focus()}
      onExitTransitionEnd={() => restoreFocus(
        returnFocusRef?.current ?? null,
        fallbackFocusRef?.current ?? null,
      )}
      opened={opened && ownerView !== null}
      position="right"
      returnFocus={false}
      size="min(34rem, 100vw)"
      title={ownerView ? `Manage access for ${ownerView.name}` : "Manage access"}
      trapFocus
    >
      <Stack gap="md">
        <Text c="dimmed" size="sm">
          Add an existing account by its exact email address. Access to this view does not grant access to report data.
        </Text>

        {atCapacity ? (
          <Text role="status" size="sm">This view cannot accept another collaborator.</Text>
        ) : null}

        <form onSubmit={submitAdd}>
          <Stack gap="xs">
            <TextInput
              autoComplete="email"
              disabled={loadFailed || loading || pending || atCapacity}
              label="Collaborator email"
              maxLength={320}
              onChange={(event) => setEmail(event.currentTarget.value)}
              ref={emailRef}
              type="email"
              value={email}
            />
            <NativeSelect
              aria-label="New collaborator access"
              data={[
                { label: "Viewer", value: "VIEW" },
                { label: "Editor", value: "EDIT" },
              ]}
              disabled={loadFailed || loading || pending || atCapacity}
              label="Access"
              onChange={(event) => setPermission(event.currentTarget.value as SavedViewPermission)}
              value={permission}
            />
            <Button
              disabled={loadFailed || loading || pending || atCapacity || email.trim().length === 0}
              loading={mutation?.kind === "add"}
              type="submit"
            >
              Add collaborator
            </Button>
          </Stack>
        </form>

        {error ? <Alert color="red" role="alert">{error}</Alert> : null}

        {loading ? (
          <Group aria-live="polite" role="status">
            <Loader size="sm" /> Loading collaborators…
          </Group>
        ) : loadFailed ? null : collaborators.length === 0 ? (
          <Text aria-live="polite" c="dimmed" role="status">Only the owner has access.</Text>
        ) : (
          <Stack aria-label="Current collaborators" gap="xs" role="list">
            {collaborators.map((collaborator) => (
              <Group
                align="end"
                className="saved-view-collaborator-row"
                justify="space-between"
                key={collaborator.userId}
                role="listitem"
                wrap="nowrap"
              >
                <Stack className="saved-view-collaborator-row__identity" gap={2} miw={0}>
                  <Text truncate="end">{collaborator.email}</Text>
                  <Text c="dimmed" size="xs">
                    Added {new Date(collaborator.createdAt).toLocaleDateString()}
                  </Text>
                </Stack>
                <Group className="saved-view-collaborator-row__actions" gap="xs" wrap="nowrap">
                  <NativeSelect
                    aria-label={`Access for ${collaborator.email}`}
                    data={[
                      { label: "Viewer", value: "VIEW" },
                      { label: "Editor", value: "EDIT" },
                    ]}
                    disabled={pending}
                    onChange={(event) => {
                      if (ownerView === null || mutationRef.current !== null) return;
                      const nextPermission = event.currentTarget.value as SavedViewPermission;
                      if (nextPermission === collaborator.permission) return;
                      const viewId = ownerView.id;
                      void runMutation(
                        { kind: "update", userId: collaborator.userId },
                        viewId,
                        () => api.updateSavedViewCollaborator(
                            viewId,
                            collaborator.userId,
                            { permission: nextPermission },
                          ),
                        (changed) => {
                          setCollaborators((current) => replaceCollaborator(
                            current,
                            changed as SavedViewCollaborator,
                          ));
                        },
                      );
                    }}
                    value={collaborator.permission}
                  />
                  <Button
                    aria-label={`Remove ${collaborator.email}`}
                    color="red"
                    disabled={pending}
                    onClick={(event) => {
                      removalTriggerRef.current = event.currentTarget;
                      setConfirmingRemoval(collaborator);
                    }}
                    size="compact-sm"
                    variant="subtle"
                  >
                    Remove
                  </Button>
                </Group>
              </Group>
            ))}
          </Stack>
        )}

        {confirmingRemoval ? (
          <Alert
            color="red"
            role="alertdialog"
            title={`Remove ${confirmingRemoval.email}?`}
          >
            <Stack gap="xs">
              <Text size="sm">They will immediately lose access to this saved view.</Text>
              <Group gap="xs">
                <Button
                  color="red"
                  disabled={pending}
                  loading={mutation?.kind === "remove"
                    && mutation.userId === confirmingRemoval.userId}
                  onClick={() => {
                    if (ownerView === null || mutationRef.current !== null) return;
                    const removed = confirmingRemoval;
                    const viewId = ownerView.id;
                    void runMutation(
                      { kind: "remove", userId: removed.userId },
                      viewId,
                      () => api.removeSavedViewCollaborator(viewId, removed.userId),
                      () => {
                        setCollaborators((current) => current.filter(
                          (candidate) => candidate.userId !== removed.userId,
                        ));
                        setConfirmingRemoval(null);
                        requestAnimationFrame(() => emailRef.current?.focus());
                      },
                    );
                  }}
                  ref={confirmRemovalRef}
                  size="compact-sm"
                >
                  Confirm removal
                </Button>
                <Button
                  disabled={pending}
                  onClick={() => {
                    const target = removalTriggerRef.current;
                    setConfirmingRemoval(null);
                    requestAnimationFrame(() => restoreFocus(target, emailRef.current));
                  }}
                  size="compact-sm"
                  variant="subtle"
                >
                  Keep collaborator
                </Button>
              </Group>
            </Stack>
          </Alert>
        ) : null}
      </Stack>
    </Drawer>
  );
}
